// src/Pages/Estabilishment/EstablishmentProfile/index.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { getMyProfile, updateMyProfile, changePassword, deleteMyAccount } from "../../../Services/Profile";
import { useT } from "../../../i18n";
import { resetLang } from "../../../i18n/lang";

// ─── Icons ────────────────────────────────────────────────────────────────────
const ic = (p: React.ReactNode, s = 16) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p}</svg>
);
const IconEye    = () => ic(<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>);
const IconEyeOff = () => ic(<><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>);
const IconChevD  = () => ic(<><polyline points="6 9 12 15 18 9"/></>);

// ─── Masks ────────────────────────────────────────────────────────────────────

/** DD/MM/YYYY */
const maskDate = (v: string): string => {
  const d = v.replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

// ─── Constants ────────────────────────────────────────────────────────────────
const LANGUAGES = [
  "Português Brasileiro","Português Europeu","Inglês","Espanhol","Francês",
  "Italiano","Alemão","Japonês","Mandarim","Árabe","Russo","Coreano","Hindi",
];

// ─── Styles ───────────────────────────────────────────────────────────────────
const inp = (extra?: React.CSSProperties): React.CSSProperties => ({
  width: "100%", padding: "9px 12px", border: "1.5px solid #e8e8e8",
  borderRadius: 8, fontSize: 13, color: "#333", outline: "none",
  background: "#fff", boxSizing: "border-box", ...extra,
});
const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 4, display: "block" };
const req = <span style={{ color: "#ef4444" }}> *</span>;
const row  = (cols: string, mb = 14): React.CSSProperties => ({ display: "grid", gridTemplateColumns: cols, gap: 12, marginBottom: mb });
const divider: React.CSSProperties = { border: "none", borderTop: "1px solid #f0f0f0", margin: "22px 0" };

// ─── Field wrapper ────────────────────────────────────────────────────────────
const FW: React.FC<{ label: string; required?: boolean; children: React.ReactNode; style?: React.CSSProperties }> = ({ label, required, children, style }) => (
  <div style={{ display: "flex", flexDirection: "column", ...style }}>
    <label style={lbl}>{label}{required && req}</label>
    {children}
  </div>
);

// ─── Password input ───────────────────────────────────────────────────────────
const PwdInput: React.FC<{ placeholder?: string; value: string; onChange: (v: string) => void }> = ({ placeholder, value, onChange }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input type={show ? "text" : "password"} placeholder={placeholder} value={value}
        onChange={e => onChange(e.target.value)}
        style={{ ...inp(), paddingRight: 36 }} />
      <button type="button" onClick={() => setShow(v => !v)}
        style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#00b6bc", display: "flex" }}>
        {show ? <IconEyeOff /> : <IconEye />}
      </button>
    </div>
  );
};

// ─── Select ───────────────────────────────────────────────────────────────────
const Sel: React.FC<{ label: string; required?: boolean; value: string; onChange: (v: string) => void; options: string[]; placeholder?: string }> = ({ label, required, value, onChange, options, placeholder }) => {
  const t = useT();
  return (
    <FW label={label} required={required}>
      <div style={{ position: "relative" }}>
        <select value={value} onChange={e => onChange(e.target.value)} style={{ ...inp(), appearance: "none", paddingRight: 30, cursor: "pointer" }}>
          <option value="">{placeholder ?? t("Selecionar")}</option>
          {options.map(o => <option key={o} value={o}>{t(o)}</option>)}
        </select>
        <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#aaa", pointerEvents: "none" }}><IconChevD /></span>
      </div>
    </FW>
  );
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: "#111" }}>{children}</h3>
);

// ─── Delete password field ────────────────────────────────────────────────────
const DelPwdField: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const t = useT();
  const [show, setShow] = useState(false);
  return (
    <FW label={t("Confirme sua senha")} required>
      <div style={{ position: "relative" }}>
        <input type={show ? "text" : "password"} value={value} onChange={e => onChange(e.target.value)}
          placeholder={t("Confirme sua senha")} style={{ ...inp({ minWidth: 200 }), paddingRight: 36 }} />
        <button type="button" onClick={() => setShow(v => !v)}
          style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#00b6bc", display: "flex" }}>
          {show ? <IconEyeOff /> : <IconEye />}
        </button>
      </div>
    </FW>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
export const EstablishmentProfile = () => {
  const t = useT();
  const navigate = useNavigate();

  // Campos existentes na modelagem (User + Profile)
  const [fullName,   setFullName]   = useState("");   // Profile.fullName
  const [nickname,   setNickname]   = useState("");   // Profile.nickname
  const [birthDate,  setBirthDate]  = useState("");   // Profile.birthDate  — máscara DD/MM/YYYY
  const [email,      setEmail]      = useState("");   // User.email
  const [nativeLang, setNativeLang] = useState("");   // User.locale

  // Endereço (Profile.address)
  const [postalCode, setPostalCode] = useState("");
  const [street,     setStreet]     = useState("");
  const [number,     setNumber]     = useState("");
  const [complement, setComplement] = useState("");
  const [district,   setDistrict]   = useState("");
  const [city,       setCity]       = useState("");
  const [uf,         setUf]         = useState("");
  const [cepLoading, setCepLoading] = useState(false);

  // Alterar senha
  const [curPwd,  setCurPwd]  = useState("");
  const [newPwd,  setNewPwd]  = useState("");
  const [confPwd, setConfPwd] = useState("");

  // Excluir conta
  const [delCheck, setDelCheck] = useState(false);
  const [delPwd,   setDelPwd]   = useState("");

  // Loading
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [pwdLoad,  setPwdLoad]  = useState(false);
  const [delLoad,  setDelLoad]  = useState(false);

  useEffect(() => {
    getMyProfile()
      .then((res: any) => {
        const d = res?.data?.data;
        if (!d) return;
        setFullName(d.fullName  ?? "");
        setNickname(d.nickname  ?? "");
        setBirthDate(d.birthDate ?? "");
        setEmail(d.email        ?? "");
        setNativeLang(d.nativeLanguage ?? "");
        const addr = d.address;
        if (addr) {
          setPostalCode(addr.postalCode ?? "");
          setStreet(addr.street         ?? "");
          setNumber(addr.number         ?? "");
          setComplement(addr.complement ?? "");
          setDistrict(addr.district     ?? "");
          setCity(addr.city?.name       ?? "");
          setUf(addr.state?.shortName ?? addr.state?.name ?? "");
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // ── CEP auto-fill (ViaCEP) ────────────────────────────────────────────────
  const maskCep = (v: string) => {
    const d = v.replace(/\D/g, "").slice(0, 8);
    return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
  };

  const handleCepBlur = async () => {
    const cep = postalCode.replace(/\D/g, "");
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const d   = await res.json();
      if (!d.erro) {
        setStreet(d.logradouro ?? "");
        setDistrict(d.bairro   ?? "");
        setCity(d.localidade   ?? "");
        setUf(d.uf             ?? "");
      }
    } catch (_) { /* silencioso */ }
    finally { setCepLoading(false); }
  };

  const handleSave = () => {
    setSaving(true);
    updateMyProfile({
      fullName, nickname, birthDate, email, nativeLanguage: nativeLang,
      address: {
        postalCode:  postalCode.replace(/\D/g, ""),
        street:      street.trim(),
        number:      number.trim(),
        complement:  complement.trim(),
        district:    district.trim(),
        city:        city.trim(),
        state:       uf.trim(),
        stateShort:  uf.trim(),
        country:     "Brasil",
      },
    })
      .catch(() => {}).finally(() => setSaving(false));
  };

  const handleChangePwd = () => {
    if (!curPwd || !newPwd) {
      toast.warning(t("Preencha a senha atual e a nova senha."));
      return;
    }
    if (newPwd.length < 8) {
      toast.warning(t("A nova senha deve ter no mínimo 8 caracteres."));
      return;
    }
    if (newPwd !== confPwd) {
      toast.warning(t("As novas senhas não coincidem."));
      return;
    }
    setPwdLoad(true);
    changePassword({ currentPassword: curPwd, newPassword: newPwd, confirmPassword: confPwd })
      .then(() => { setCurPwd(""); setNewPwd(""); setConfPwd(""); })
      .catch(() => {}) // toast de erro já exibido pelo service
      .finally(() => setPwdLoad(false));
  };

  const handleDelete = () => {
    if (!delCheck) { toast.warning(t("Marque a confirmação para excluir a conta.")); return; }
    if (!delPwd)   { toast.warning(t("Informe sua senha para confirmar.")); return; }
    setDelLoad(true);
    deleteMyAccount(delPwd)
      // resetLang junto: o navigate é do react-router e NÃO recarrega a
      // página, então sem isto o idioma do usuário excluído continuaria
      // valendo para quem entrasse em seguida
      .then(() => { localStorage.clear(); resetLang(); navigate("/login"); })
      .catch(() => {}) // toast de erro já exibido pelo service
      .finally(() => setDelLoad(false));
  };

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", paddingTop: 60 }}>
      <CircularProgress style={{ color: "#00b6bc" }} />
    </div>
  );

  const showPwdBtn = curPwd || newPwd || confPwd;
  const canDelete  = delCheck && delPwd;

  return (
    <div>
      <h2 style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 700, color: "#111" }}>{t("Meu perfil")}</h2>
      <p style={{ margin: "0 0 24px", fontSize: 13, color: "#888" }}>{t("Edite suas informações de perfil.")}</p>

      <div style={{ background: "#fff", border: "1px solid #f0f0f0", borderRadius: 14, padding: "28px 32px", maxWidth: 860 }}>

        {/* ── Dados pessoais ────────────────────────────────────────────── */}
        <SectionTitle>{t("Dados pessoais")}</SectionTitle>

        {/* Row 1: Nome completo | Apelido | Data de nascimento */}
        <div style={row("2fr 1fr 1fr")}>
          <FW label={t("Nome completo")} required>
            <input
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder={t("Nome e Sobrenome")}
              style={inp()}
            />
          </FW>

          <FW label={t("Apelido")}>
            <input
              value={nickname}
              onChange={e => setNickname(e.target.value)}
              placeholder={t("Como prefere ser chamado")}
              style={inp()}
            />
          </FW>

          <FW label={t("Data de nascimento")} required>
            <input
              value={birthDate}
              onChange={e => setBirthDate(maskDate(e.target.value))}
              placeholder={t("DD/MM/AAAA")}
              maxLength={10}
              inputMode="numeric"
              style={inp()}
            />
          </FW>
        </div>

        {/* Row 2: E-mail | Linguagem nativa */}
        <div style={row("1fr 1fr")}>
          <FW label={t("E-mail")} required>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="exemplo@email.com"
              style={inp()}
            />
          </FW>

          <Sel
            label={t("Linguagem nativa")}
            required
            value={nativeLang}
            onChange={setNativeLang}
            options={LANGUAGES}
            placeholder={t("Selecionar idioma")}
          />
        </div>

        <hr style={divider} />

        {/* ── Endereço ──────────────────────────────────────────────────── */}
        <SectionTitle>{t("Endereço")}</SectionTitle>

        <div style={row("1fr")}>
          <FW label={t("CEP")}>
            <div style={{ position: "relative" }}>
              <input
                value={postalCode}
                onChange={e => setPostalCode(maskCep(e.target.value))}
                onBlur={handleCepBlur}
                placeholder="00000-000"
                maxLength={9}
                inputMode="numeric"
                style={inp()}
              />
              {cepLoading && <CircularProgress size={13} style={{ color: "#00b6bc", position: "absolute", right: 12, top: 11 }} />}
            </div>
          </FW>
        </div>

        <div style={row("1fr 100px")}>
          <FW label={t("Endereço")}>
            <input value={street} onChange={e => setStreet(e.target.value)} placeholder={t("Rua, Av...")} style={inp()} />
          </FW>
          <FW label={t("Número")}>
            <input value={number} onChange={e => setNumber(e.target.value)} placeholder={t("Nº")} style={inp()} />
          </FW>
        </div>

        <div style={row("1fr 1fr")}>
          <FW label={t("Complemento")}>
            <input value={complement} onChange={e => setComplement(e.target.value)} placeholder={t("Apto, Sala...")} style={inp()} />
          </FW>
          <FW label={t("Bairro")}>
            <input value={district} onChange={e => setDistrict(e.target.value)} placeholder={t("Bairro")} style={inp()} />
          </FW>
        </div>

        <div style={row("1fr 80px")}>
          <FW label={t("Cidade")}>
            <input value={city} onChange={e => setCity(e.target.value)} placeholder={t("Cidade")} style={inp()} />
          </FW>
          <FW label={t("UF")}>
            <input value={uf} onChange={e => setUf(e.target.value.toUpperCase())} placeholder="SP" maxLength={2} style={inp()} />
          </FW>
        </div>

        <hr style={divider} />

        {/* ── Alterar senha ─────────────────────────────────────────────── */}
        <SectionTitle>{t("Alterar senha")}</SectionTitle>

        <div style={row("1fr 1fr 1fr", showPwdBtn ? 10 : 0)}>
          <FW label={t("Senha atual")} required>
            <PwdInput placeholder={t("Senha atual")} value={curPwd} onChange={setCurPwd} />
          </FW>
          <FW label={t("Nova senha")} required>
            <PwdInput placeholder={t("Nova senha")} value={newPwd} onChange={setNewPwd} />
          </FW>
          <FW label={t("Confirmar nova senha")} required>
            <PwdInput placeholder={t("Confirme a nova senha")} value={confPwd} onChange={setConfPwd} />
          </FW>
        </div>

        {showPwdBtn && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
            <button onClick={handleChangePwd} disabled={pwdLoad}
              style={{ padding: "8px 20px", background: "#00b6bc", border: "none", borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              {pwdLoad && <CircularProgress size={12} color="inherit" />} {t("Alterar senha")}
            </button>
          </div>
        )}

        <hr style={divider} />

        {/* ── Excluir conta ─────────────────────────────────────────────── */}
        <SectionTitle>{t("Excluir conta")}</SectionTitle>

        <p style={{ fontSize: 13, color: "#555", margin: "0 0 14px", lineHeight: 1.6 }}>
          {t("Ao excluir sua conta, todos os seus dados serão permanentemente removidos. Sua assinatura também será cancelada.")}
        </p>

        <div style={{ display: "flex", alignItems: "flex-end", gap: 14, flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", maxWidth: 320, flex: 1 }}>
            <input type="checkbox" checked={delCheck} onChange={e => setDelCheck(e.target.checked)}
              style={{ marginTop: 3, width: 16, height: 16, accentColor: "#ef4444", flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: "#555", lineHeight: 1.5 }}>
              {t("Estou ciente que meus dados serão removidos e desejo excluir minha conta.")}
            </span>
          </label>

          <DelPwdField value={delPwd} onChange={setDelPwd} />

          <button onClick={handleDelete} disabled={delLoad || !canDelete}
            style={{ padding: "9px 20px", background: canDelete ? "#ef4444" : "#f5c6c6", border: "none", borderRadius: 9, color: "#fff", fontWeight: 700, fontSize: 13, cursor: canDelete ? "pointer" : "not-allowed", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", marginBottom: 1 }}>
            {delLoad && <CircularProgress size={12} color="inherit" />} {t("Excluir conta")}
          </button>
        </div>

        <hr style={divider} />

        {/* ── Salvar ────────────────────────────────────────────────────── */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={() => navigate(-1)}
            style={{ padding: "10px 28px", background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: "pointer", color: "#555" }}>
            {t("Voltar")}
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: "10px 28px", background: "#00b6bc", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", color: "#fff", display: "flex", alignItems: "center", gap: 7 }}>
            {saving && <CircularProgress size={14} color="inherit" />} {t("Salvar")}
          </button>
        </div>

      </div>
    </div>
  );
};